import type { Query } from "@tanstack/react-query"
import type { AxiosError } from "axios"

/**
 * Default per-query retry attempts before the predicate gives up.
 * Matches React Query's own default so the constant is the only piece
 * we tweak when the policy changes.
 */
const DEFAULT_RETRY_ATTEMPTS = 3

/**
 * React Query `retry` predicate that skips 4xx responses while still
 * applying the standard transient-failure backoff to 5xx and
 * non-HTTP errors.
 *
 * The library's default policy retries every failure 3 times. That's
 * the right behaviour for transient network blips and 5xx replies
 * (they may resolve on the next attempt), but it's pathological for
 * 4xx: a 404 on a deleted resource, a 403 on a permission change, a
 * 400 on a malformed param — none of these recover by retrying.
 * Three retries per Get just buys three console errors per page open
 * and three Sentry events per stale link click.
 *
 * Applied as the React Query client-level default in
 * `AppProviders.tsx` so every `useGet` against a deletable resource
 * picks it up for free. Per-query overrides still work; this is just
 * a better baseline.
 */
export const skipRetryOn4xx = (failureCount: number, error: unknown): boolean => {
  const status = (error as AxiosError | undefined)?.response?.status
  if (status !== undefined && status >= 400 && status < 500) return false
  return failureCount < DEFAULT_RETRY_ATTEMPTS
}

/**
 * React Query `refetchOnWindowFocus` predicate: a query whose last result was a
 * **403** is not re-issued when the tab regains focus.
 *
 * 🚨 **`skipRetryOn4xx` ABOVE CLOSES ONE MULTIPLIER AND READS AS IF IT CLOSED
 * THEM ALL.** It governs the retry loop INSIDE one fetch. Auto-refetch is a
 * separate mechanism with the same arithmetic: with the default `staleTime` of
 * 0 every window focus re-issues the request, so the same terminal refusal is
 * sent again every time a support agent alt-tabs back to the app. Measured on
 * `BACKEND-PRD-15F`: 29,257 `ForbiddenException` events over 22 users, on two
 * endpoints, from a population whose permissions never changed once.
 *
 * **403 ONLY, and the narrowness is the point.** The other 4xx codes are not
 * terminal in the same way and suppressing them would cost real recoveries:
 * a 401 is cured by the very session refresh that returning to the tab
 * triggers; a 404 becomes a 200 the moment the resource is created; a 429
 * expires. A 403 is a decision about a caller the server DID recognise, and
 * nothing that happens in another browser tab changes it — only a permission
 * change does, and that arrives through a reload or an explicit invalidation,
 * neither of which this predicate is on the path of.
 *
 * Applied as the client-level default in `AppProviders.tsx`. An explicit
 * `refetch()` and `invalidateQueries` are both unaffected — this governs the
 * automatic focus refetch and nothing else.
 */
export const skipRefetchOnFocusAfter403 = (query: Query): boolean => {
  const status = (query.state.error as AxiosError | undefined)?.response?.status
  return status !== 403
}
